import React from 'react';
import {ActivityIndicator, View} from 'react-native';
import {makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';

import {Icon} from '../../../components/Icon';
import {useStores} from '../../../stores/use-stores';

/**
 * DIS-1608: the coupon's status, as a single glyph beside the code in the
 * coupon row. The row already names the code and the cart total already names
 * the amount, so the status needs no words of its own — only whether MFL has
 * accepted it, is still checking, or has refused.
 *
 * The slot is a fixed square. Every cart write queues a re-check, so the glyph
 * swaps between spinner and verdict often; a slot that resized would shift the
 * coupon code sideways on every quantity tap.
 */
const CouponStatusIcon = observer(() => {
  const {cartStore} = useStores();
  const styles = useStyles();

  if (!cartStore.currentOrder?.coupon_code) return null;

  // Only the three statuses that have something to show. 'none' means no check
  // has been asked for, and an unrecognised value must not fall through to the
  // refusal glyph — a wrong error mark is worse than no mark.
  const status = cartStore.couponStatus;
  if (status !== 'qualified' && status !== 'checking' && status !== 'unqualified') return null;

  return (
    <View testID="coupon-status-icon" style={styles.slot}>
      {status === 'checking' ? (
        <ActivityIndicator
          testID="coupon-status-icon-spinner"
          size="small"
          color={styles.spinner.color}
        />
      ) : (
        <Icon
          testID={
            status === 'qualified' ? 'coupon-status-icon-applied' : 'coupon-status-icon-refused'
          }
          name={status === 'qualified' ? 'circle-check' : 'circle-exclamation'}
          size={16}
          color={status === 'qualified' ? styles.applied.color : styles.refused.color}
        />
      )}
    </View>
  );
});

const useStyles = makeStyles(theme => ({
  slot: {
    // The spinner is 20pt on iOS and 16-20pt on Android and web; the icon is
    // 16pt. A fixed box keeps the code's left edge still across all three.
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  spinner: {color: theme.colors.primary},
  applied: {color: theme.colors.success},
  refused: {color: theme.colors.error},
}));

export default CouponStatusIcon;
